/**
 * Phone numbers, said one way everywhere (#635).
 *
 * The store is in India and nearly every number is an Indian mobile, written
 * every way Shopify, a checkout app or a person typing it allows: "9646407795",
 * "09646407795", "+91 96464-07795", "91 9646407795". Vendors were shown without
 * the +91 and customers with it. This reads all of them as one number and
 * writes it as `+91 96464 07795`. A number that is not a ten-digit Indian one —
 * a landline with an STD code, a foreign number — is shown as it was given
 * rather than guessed at.
 */

const INDIA = "91";

/**
 * The ten digits of an Indian mobile, or null if this is not one. A mobile
 * starts 6 to 9; a landline with its STD code ("022 2345 6789") does not, and
 * is left as written.
 */
function indianTenDigits(raw: string): string | null {
  const digits = raw.replace(/\D/g, "");
  const ten =
    digits.length === 10
      ? digits
      : digits.length === 11 && digits.startsWith("0")
        ? digits.slice(1)
        : digits.length === 12 && digits.startsWith(INDIA)
          ? digits.slice(2)
          : null;
  return ten !== null && /^[6-9]/.test(ten) ? ten : null;
}

/** `+91 96464 07795`, or the number as given when it is not an Indian mobile. */
export function formatPhone(raw: string): string {
  const ten = indianTenDigits(raw);
  if (ten === null) return raw.trim();
  return `+${INDIA} ${ten.slice(0, 5)} ${ten.slice(5)}`;
}

/** The number with its country code and nothing else: `919646407795`. */
export function phoneDigits(raw: string): string {
  const ten = indianTenDigits(raw);
  if (ten !== null) return `${INDIA}${ten}`;
  return raw.replace(/\D/g, "");
}

/** What the dialler opens: `tel:+919646407795`. */
export function telHref(raw: string): string {
  return `tel:+${phoneDigits(raw)}`;
}

/** A WhatsApp chat with the number, country code 91 when it was missing. */
export function whatsAppHref(raw: string): string {
  return `https://wa.me/${phoneDigits(raw)}`;
}
